import React, { useState, useRef } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
  useMediaQuery,
  useTheme,
  IconButton,
  Menu,
  MenuItem
} from '@mui/material';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
import GridViewIcon from '@mui/icons-material/GridView';
import DownloadIcon from '@mui/icons-material/Download';
import AspectSentimentTable from './AspectSentimentTable';
// import html2canvas from 'html2canvas';
import pptxgen from 'pptxgenjs';
import captureScreenshot from 'utils/screenshotUtils';

/**
 * Component that visualizes aspect sentiment analysis as an interactive matrix
 * Takes product_aspects data from v2 reviews analysis
 */
const AspectSentimentMatrix = ({ aspectData = [] }) => {
  const [view, setView] = useState('graph');
  const [showQuotes, setShowQuotes] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const theme = useTheme();
  const lg = useMediaQuery(theme.breakpoints.up('lg'));
  const contentRef = useRef(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleViewChange = (event, newView) => {
    if (newView !== null) {
      setView(newView);
    }
  };

  const setGridCardBackgrounds = (add) => {
    // Find all grid cards
    const gridCards = contentRef.current?.querySelectorAll('.aspect-grid-card');
    if (!gridCards) return;
    gridCards.forEach((card) => {
      if (add) {
        // Determine sentiment from data-sentiment attribute
        const sentiment = card.getAttribute('data-sentiment');
        if (sentiment === 'positive') card.style.background = '#E3FCEC';
        else if (sentiment === 'negative') card.style.background = '#FDECEA';
        else card.style.background = '#F5F5F5';
      } else {
        card.style.background = '';
      }
    });
  };

  const downloadAsPng = () => {
    if (!contentRef.current) return;

    // For grid view, set hard-coded backgrounds first
    if (view === 'grid') setGridCardBackgrounds(true);

    captureScreenshot({
      contentRef,
      filename: 'aspect-sentiment-analysis',
      afterCapture: () => {
        // Remove grid card backgrounds if needed
        if (view === 'grid') setGridCardBackgrounds(false);
        handleClose();
      }
    });
  };

  const downloadAsPPTX = async () => {
    if (!aspectData.length) return;

    // Create a new presentation
    const pptx = new pptxgen();

    // Constants for table layout
    const ROWS_PER_SLIDE = 5; // Maximum number of rows per slide (including header)
    const totalSlides = Math.ceil(aspectData.length / ROWS_PER_SLIDE);

    // Create slides
    for (let slideIndex = 0; slideIndex < totalSlides; slideIndex++) {
      const slide = pptx.addSlide();

      // Set slide background
      slide.background = { color: 'FFFFFF' };

      // Add title
      slide.addText('Aspect Sentiment Analysis', {
        x: 0.5,
        y: 0.5,
        w: '90%',
        h: 1,
        fontSize: 24,
        color: '363636',
        align: 'center',
        bold: true
      });

      // Add subtitle
      const subtitle =
        totalSlides > 1
          ? `Key product aspects and their sentiment scores (${slideIndex + 1}/${totalSlides})`
          : 'Key product aspects and their sentiment scores';

      slide.addText(subtitle, {
        x: 0.5,
        y: 1.2,
        w: '90%',
        h: 0.5,
        fontSize: 14,
        color: '666666',
        align: 'center',
        italic: true
      });

      // Add "Powered by Askpot" branding as a horizontal row at the top right
      // 1. Powered by
      slide.addText('Powered by', {
        x: 7.0,
        y: 0.5,
        w: 1.1,
        h: 0.4,
        fontSize: 10,
        color: '666666',
        align: 'right',
        isTextBox: true
      });
      // 2. Logo (favicon.svg)
      slide.addImage({
        path: '/favicon.svg',
        x: 8.15,
        y: 0.51,
        w: 0.3,
        h: 0.3
      });
      // 3. Askpot text
      slide.addText('Askpot', {
        x: 8.5,
        y: 0.5,
        w: 1,
        h: 0.4,
        fontSize: 13,
        color: '363636',
        bold: true,
        align: 'left',
        isTextBox: true
      });

      // Calculate data range for this slide
      const startIndex = slideIndex * (ROWS_PER_SLIDE - 1); // -1 to account for header row
      const endIndex = Math.min(startIndex + (ROWS_PER_SLIDE - 1), aspectData.length);
      const slideData = aspectData.slice(startIndex, endIndex);

      // Create table data
      const tableData = [
        ['Aspect', 'Sentiment Score', 'Priority', 'Mentions', 'Key Insight'],
        ...slideData.map((item) => [
          item.aspect,
          item.sentiment_score.toFixed(1),
          item.business_priority || 'N/A',
          item.mentions_count.toString(),
          item.key_insight
        ])
      ];

      // Add table
      slide.addTable(tableData, {
        x: 0.25,
        y: 2,
        w: '95%',
        h: '70%',
        colW: [2.5, 1.2, 1.2, 1.2, 3.5],
        rowH: 0.4,
        margin: 0,
        border: { type: 'solid', color: 'CCCCCC', pt: 1 },
        fontSize: 11,
        color: '363636',
        align: 'left',
        valign: 'middle',
        headerRows: 1,
        headerColor: 'F5F5F5',
        headerFontSize: 11,
        headerAlign: 'center',
        headerBold: true,
        autoPage: false,
        autoPageRepeatHeader: false,
        lineSize: 0.5
      });

      // Add footer
      slide.addText('Generated by Askpot.com', {
        x: 0.5,
        y: '90%',
        w: '90%',
        h: 0.5,
        fontSize: 10,
        color: '999999',
        align: 'center'
      });
    }

    // Save the presentation
    pptx.writeFile({ fileName: 'aspect-sentiment-analysis.pptx' });
    handleClose();
  };

  const convertToRTF = (data) => {
    let rtf = '{\\rtf1\\ansi\\deff0{\\fonttbl{\\f0 Arial;}}\n';

    // Escape RTF characters
    const escapeRTF = (text) => {
      if (typeof text !== 'string') {
        text = JSON.stringify(text) || '';
      }
      return text.replace(/\\/g, '\\\\').replace(/{/g, '\\{').replace(/}/g, '\\}');
    };

    // Add title
    rtf += '\\b\\fs24 Aspect Sentiment Analysis\\b0\\fs20\\par\\par\n';
    rtf += '\\i Key product aspects and their sentiment scores\\i0\\par\\par\n';

    // Add table
    rtf += '\\trowd\\trgaph108\\trleft0\\trqc\n';
    rtf += '\\cellx1000\\cellx2000\\cellx3000\\cellx4000\\cellx5000\n';
    rtf +=
      '\\pard\\intbl\\b Aspect\\b0\\cell\\b Sentiment Score\\b0\\cell\\b Priority\\b0\\cell\\b Mentions\\b0\\cell\\b Key Insight\\b0\\cell\\row\n';

    data.forEach((item) => {
      rtf += '\\trowd\\trgaph108\\trleft0\\trqc\n';
      rtf += '\\cellx1000\\cellx2000\\cellx3000\\cellx4000\\cellx5000\n';
      rtf += `\\pard\\intbl ${escapeRTF(item.aspect)}\\cell ${escapeRTF(item.sentiment_score.toFixed(1))}\\cell ${escapeRTF(
        item.business_priority || 'N/A'
      )}\\cell ${escapeRTF(item.mentions_count)}\\cell ${escapeRTF(item.key_insight)}\\cell\\row\n`;
    });

    rtf += '}';
    return rtf;
  };

  const downloadAsDoc = () => {
    if (!aspectData.length) return;

    const rtfContent = convertToRTF(aspectData);
    const blob = new Blob([rtfContent], { type: 'application/msword' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'aspect-sentiment-analysis.doc';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    handleClose();
  };

  // If no data is provided, don't render the component
  if (aspectData.length === 0) {
    return null;
  }

  // Sort aspects by mentions count (ascending) for better visualization
  const sortedAspects = [...aspectData].sort((a, b) => a.mentions_count - b.mentions_count);

  // Calculate sentiment score range with padding
  const sentimentScores = sortedAspects.map((item) => item.sentiment_score);
  const minSentiment = Math.max(-1, Math.min(...sentimentScores) - 0.9);
  const maxSentiment = Math.min(1, Math.max(...sentimentScores) + 0.9);
  const sentimentRange = maxSentiment - minSentiment;

  // Calculate min and max mentions for dot size scaling
  // const mentionsCounts = sortedAspects.map(item => item.mentions_count);
  // const minMentions = Math.min(...mentionsCounts);
  // const maxMentions = Math.max(...mentionsCounts);

  // Calculate dot size based on mentions count
  // const getDotSize = (mentions) => {
  //   const minSize = 8; // minimum dot size
  //   const maxSize = 64; // maximum dot size
  //   const scale = (mentions - minMentions) / (maxMentions - minMentions);
  //   return minSize + (maxSize - minSize) * scale;
  // };

  // Calculate positions and determine card placements to avoid overflow
  const aspectPositions = sortedAspects.map((aspect, index, array) => {
    // Calculate horizontal position
    const xPos = (index / Math.max(array.length - 1, 1)) * 100;

    // Calculate vertical position based on sentiment score with adjusted range
    // Ensure the score is clamped between -1 and 1
    const clampedScore = Math.max(-1, Math.min(1, aspect.sentiment_score));
    const yPos = (1 - (clampedScore - minSentiment) / sentimentRange) * 100;

    // Determine card position based on sentiment score
    // Higher sentiment scores (more positive) get cards below
    // Lower sentiment scores (more negative) get cards above
    const cardPosition = clampedScore > 0 ? 'bottom' : 'top';

    return {
      ...aspect,
      xPos,
      yPos,
      cardPosition
    };
  });

  const getNodeColor = (score) => {
    if (score > 0.5) return '#4caf50'; // green
    if (score > 0) return '#2196f3'; // blue
    if (score > -0.5) return '#ffeb3b'; // yellow
    return '#f44336'; // red
  };

  const renderGraph = () => (
    <Card sx={{ width: '100%', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', borderRadius: '16px' }}>
      <CardContent ref={contentRef}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" gutterBottom sx={{ mb: 0 }}>
            Aspect Sentiment Analysis
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Box className="screenshot-controls">
              <ToggleButton
                value="quotes"
                selected={showQuotes}
                onChange={() => setShowQuotes(!showQuotes)}
                size="small"
                sx={{
                  height: 28,
                  px: 1,
                  fontSize: '0.75rem'
                }}
              >
                {showQuotes ? 'Hide Quotes' : 'Show Quotes'}
              </ToggleButton>
            </Box>
            <Box className="screenshot-controls">
              <IconButton onClick={handleClick} size="small" sx={{ height: 28 }} aria-label="download options">
                <DownloadIcon fontSize="small" />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                PaperProps={{
                  sx: {
                    border: 1,
                    borderColor: 'secondary.light',
                    borderRadius: 1
                  }
                }}
              >
                <MenuItem onClick={downloadAsPng}>Download as PNG</MenuItem>
                <MenuItem onClick={downloadAsDoc}>Download as DOC</MenuItem>
                <MenuItem onClick={downloadAsPPTX}>Download as PPTX</MenuItem>
              </Menu>
            </Box>
            <Box className="screenshot-controls">
              <ToggleButtonGroup value={view} exclusive onChange={handleViewChange} size="small">
                <ToggleButton value="graph" aria-label="graph view">
                  <TimelineOutlinedIcon />
                </ToggleButton>
                <ToggleButton value="table" aria-label="table view">
                  <TableChartOutlinedIcon />
                </ToggleButton>
                <ToggleButton value="grid" aria-label="grid view">
                  <GridViewIcon />
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>
        </Box>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Key product aspects and their sentiment scores
        </Typography>

        <Box
          sx={{
            position: 'relative',
            width: '100%',
            pl: '40px',
            height: '600px',
            mt: 2,
            mb: 2
          }}
        >
          {/* Y-axis label */}
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              top: '50%',
              transform: 'translateY(-50%) rotate(-90deg)',
              transformOrigin: 'left center',
              color: 'text.secondary',
              fontSize: '0.75rem',
              fontWeight: 500,
              letterSpacing: 1,
              zIndex: 2
            }}
          >
            Sentiment
          </Box>
          {/* Y-axis labels */}
          <Box
            sx={{
              position: 'absolute',
              left: 30,
              top: 0,
              bottom: '35px',
              width: '50px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              fontSize: '0.75rem',
              color: 'text.secondary',
              py: 2
            }}
          >
            <Typography variant="caption">{maxSentiment.toFixed(1)}</Typography>
            <Typography variant="caption">{((maxSentiment + minSentiment) / 2).toFixed(1)}</Typography>
            <Typography variant="caption">{minSentiment.toFixed(1)}</Typography>
          </Box>

          {/* Horizontal grid lines */}
          <Box
            sx={{
              position: 'absolute',
              left: '80px',
              right: '50px',
              top: 0,
              bottom: '40px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              py: 2
            }}
          >
            <Box sx={{ borderTop: '1px solid #e0e0e0', width: '100%' }}></Box>
            <Box sx={{ borderTop: '1px solid #e0e0e0', width: '100%' }}></Box>
            <Box sx={{ borderTop: '1px solid #e0e0e0', width: '100%' }}></Box>
          </Box>

          {/* X-axis label */}
          <Box
            sx={{
              position: 'absolute',
              left: '50%',
              bottom: 0,
              transform: 'translateX(-50%)',
              color: 'text.secondary',
              fontSize: '13px',
              fontWeight: 500,
              letterSpacing: 1,
              zIndex: 2
            }}
          >
            Frequency
          </Box>

          {/* Visualization area */}
          <Box
            sx={{
              position: 'absolute',
              left: '80px',
              right: '50px',
              top: '20px',
              bottom: '60px'
            }}
          >
            {/* Aspect nodes and cards */}
            {aspectPositions.map((item) => (
              <Box
                key={item.aspect}
                sx={{
                  position: 'absolute',
                  left: `${item.xPos}%`,
                  top: `${item.yPos}%`
                }}
              >
                {/* Node */}
                <Tooltip title={`${item.mentions_count} mentions`} arrow placement="top">
                  <Box
                    sx={{
                      width: '18px',
                      height: '18px',
                      borderRadius: '50%',
                      backgroundColor: getNodeColor(item.sentiment_score),
                      position: 'absolute',
                      transform: 'translate(-50%, -50%)',
                      zIndex: 10,
                      cursor: 'pointer'
                    }}
                  ></Box>
                </Tooltip>

                {/* Mentions count text */}
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    position: 'absolute',
                    transform: 'translateX(-50%)',
                    left: '50%',
                    ...(item.cardPosition === 'top'
                      ? {
                          bottom: '-30px'
                        }
                      : {
                          top: '-30px'
                        }),
                    whiteSpace: 'nowrap'
                  }}
                >
                  {item.mentions_count} mentions
                </Typography>

                {/* Card */}
                <Tooltip title={`${item.mentions_count} mentions`} arrow placement="top">
                  <Box
                    sx={{
                      position: 'absolute',
                      backgroundColor: 'background.paper',
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      p: 1.5,
                      width: lg ? '120px' : '100px',
                      zIndex: 20,
                      cursor: 'pointer',
                      // Positioning logic for all cases
                      ...(item.cardPosition === 'top' && {
                        bottom: 'calc(100% + 30px)',
                        left: '50%',
                        transform: 'translateX(-50%)'
                      }),
                      ...(item.cardPosition === 'bottom' && {
                        top: 'calc(100% + 30px)',
                        left: '50%',
                        transform: 'translateX(-50%)'
                      })
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          wordBreak: 'break-word',
                          whiteSpace: 'normal'
                        }}
                      >
                        {item.aspect}
                      </Typography>
                      {showQuotes && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            wordBreak: 'break-word',
                            whiteSpace: 'normal'
                          }}
                        >
                          {item.key_insight}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Tooltip>

                {/* Sentiment Score Badge */}
                <Box
                  sx={{
                    position: 'absolute',
                    bgcolor: 'background.paper',
                    fontSize: '0.75rem',
                    fontWeight: 'bold',
                    px: 0.75,
                    py: 0.25,
                    borderRadius: '9999px',
                    border: '1px solid',
                    borderColor: 'divider',
                    zIndex: 20,
                    transform: 'translateX(-50%)',
                    left: '50%',
                    ...(item.cardPosition === 'top' ? { top: '-40px' } : { bottom: '-40px' })
                  }}
                >
                  {item.sentiment_score.toFixed(1)}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const renderTable = () => (
    <Card sx={{ width: '100%', boxShadow: 1 }}>
      <CardContent ref={contentRef}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" gutterBottom sx={{ mb: 0 }}>
            Aspect Sentiment Analysis
          </Typography>
          <Box className="screenshot-controls" sx={{ display: 'flex', gap: 1 }}>
            <IconButton onClick={handleClick} size="small" sx={{ height: 28 }} aria-label="download options">
              <DownloadIcon fontSize="small" />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              PaperProps={{
                sx: {
                  border: 1,
                  borderColor: 'secondary.light',
                  borderRadius: 1
                }
              }}
            >
              <MenuItem onClick={downloadAsPng}>Download as PNG</MenuItem>
              <MenuItem onClick={downloadAsDoc}>Download as DOC</MenuItem>
              <MenuItem onClick={downloadAsPPTX}>Download as PPTX</MenuItem>
            </Menu>
            <ToggleButtonGroup value={view} exclusive onChange={handleViewChange} size="small">
              <ToggleButton value="graph" aria-label="graph view">
                <TimelineOutlinedIcon />
              </ToggleButton>
              <ToggleButton value="table" aria-label="table view">
                <TableChartOutlinedIcon />
              </ToggleButton>
              <ToggleButton value="grid" aria-label="grid view">
                <GridViewIcon />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>
        <AspectSentimentTable aspectData={aspectData} />
      </CardContent>
    </Card>
  );

  const renderGrid = () => {
    // Group aspects by sentiment and frequency
    const groupedAspects = aspectPositions.reduce((acc, item) => {
      let sentiment;
      if (item.sentiment_score > 0.3) sentiment = 'positive';
      else if (item.sentiment_score < -0.3) sentiment = 'negative';
      else sentiment = 'neutral';

      // Determine frequency tier based on mentions_count
      const maxMentions = Math.max(...aspectPositions.map((a) => a.mentions_count));
      let frequency;
      if (item.mentions_count >= maxMentions * 0.66) frequency = 'high';
      else if (item.mentions_count >= maxMentions * 0.33) frequency = 'medium';
      else frequency = 'low';

      const key = `${frequency}-${sentiment}`;
      if (!acc[key]) acc[key] = [];
      acc[key].push(item);
      return acc;
    }, {});

    return (
      <Card sx={{ width: '100%', boxShadow: 1 }}>
        <CardContent ref={contentRef}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" gutterBottom sx={{ mb: 0 }}>
              Aspect Sentiment Analysis
            </Typography>
            <Box className="screenshot-controls" sx={{ display: 'flex', gap: 1 }}>
              <ToggleButton
                value="quotes"
                selected={showQuotes}
                onChange={() => setShowQuotes(!showQuotes)}
                size="small"
                sx={{
                  height: 28,
                  px: 1,
                  fontSize: '0.75rem'
                }}
              >
                {showQuotes ? 'Hide Quotes' : 'Show Quotes'}
              </ToggleButton>
              <IconButton onClick={handleClick} size="small" sx={{ height: 28 }} aria-label="download options">
                <DownloadIcon fontSize="small" />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                PaperProps={{
                  sx: {
                    border: 1,
                    borderColor: 'secondary.light',
                    borderRadius: 1
                  }
                }}
              >
                <MenuItem onClick={downloadAsPng}>Download as PNG</MenuItem>
                <MenuItem onClick={downloadAsDoc}>Download as DOC</MenuItem>
                <MenuItem onClick={downloadAsPPTX}>Download as PPTX</MenuItem>
              </Menu>
              <ToggleButtonGroup value={view} exclusive onChange={handleViewChange} size="small">
                <ToggleButton value="graph" aria-label="graph view">
                  <TimelineOutlinedIcon />
                </ToggleButton>
                <ToggleButton value="table" aria-label="table view">
                  <TableChartOutlinedIcon />
                </ToggleButton>
                <ToggleButton value="grid" aria-label="grid view">
                  <GridViewIcon />
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
            {['high', 'medium', 'low'].map((frequency) =>
              ['positive', 'neutral', 'negative'].map((sentiment) => {
                const items = groupedAspects[`${frequency}-${sentiment}`] || [];

                // Create gradient background based on sentiment
                const gradientBg =
                  sentiment === 'positive'
                    ? 'linear-gradient(135deg, rgba(227, 252, 236, 0.6) 0%, rgba(227, 252, 236, 0.01) 100%)'
                    : sentiment === 'negative'
                    ? 'linear-gradient(135deg, rgba(253, 236, 234, 0.6) 0%, rgba(253, 236, 234, 0.01) 100%)'
                    : 'linear-gradient(135deg, rgba(245, 245, 245, 0.6) 0%, rgba(245, 245, 245, 0.1) 100%)';

                return (
                  <Card
                    key={`${frequency}-${sentiment}`}
                    component="div"
                    className="aspect-grid-card"
                    data-sentiment={sentiment}
                    sx={{
                      background: gradientBg,
                      p: 2,
                      minHeight: 150,
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      {sentiment === 'neutral' ? 'Mixed' : sentiment.charAt(0).toUpperCase() + sentiment.slice(1)} •{' '}
                      {frequency.charAt(0).toUpperCase() + frequency.slice(1)} Frequency
                    </Typography>
                    <Box sx={{ flex: 1 }}>
                      {items.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          No aspects in this category
                        </Typography>
                      ) : (
                        items.map((item) => {
                          // Set border color based on sentiment
                          const borderColor =
                            sentiment === 'positive' ? 'success.main' : sentiment === 'negative' ? 'error.main' : 'grey.400';

                          return (
                            <Box
                              key={item.aspect}
                              sx={{
                                mb: 1.5,
                                ml: 1,
                                pl: 2,
                                borderLeft: '3px solid',
                                borderColor: borderColor,
                                py: 0.5
                              }}
                            >
                              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                {item.aspect} ({item.mentions_count} mentions)
                              </Typography>
                              {showQuotes && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                  {item.key_insight}
                                </Typography>
                              )}
                            </Box>
                          );
                        })
                      )}
                    </Box>
                  </Card>
                );
              })
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };

  return view === 'graph' ? renderGraph() : view === 'table' ? renderTable() : renderGrid();
};

export default AspectSentimentMatrix;
